type LegacyStyle = {
	selectors: string[];
	importFn: () => Promise<unknown>;
};

/**
 * Conditionally loads legacy styles based on DOM content,
 * including dynamically added content via MutationObserver.
 */
export const loadLegacyStyles = (styles?: LegacyStyle[]) => {
	const legacyStyles = styles ?? [
		{
			selectors: ['.e-button', '.e-button__right', '.e-button__left', '.e-button--filled', '.e-button--link'],
			importFn: () => import('../styles/legacy/legacy-button.scss'),
		},
		{
			selectors: ['.c-form-latest'],
			importFn: () => import('../styles/legacy/legacy-form-latest.scss'),
		},
		{
			selectors: ['.e-form-field', '.e-form-row', '.e-form-row__field', '.e-form-label', '.e-form-select'],
			importFn: () => import('../styles/legacy/legacy-form.scss'),
		},
		{
			selectors: ['.c-form-n', '.c-form-n__field-container', '.c-form-n__row', '.c-form-n__label'],
			importFn: () => import('../styles/legacy/legacy-form-new.scss'),
		},
		{
			selectors: ['.b-grid', '.b-grid__row', '.b-grid__col'],
			importFn: () => import('../styles/legacy/legacy-grid.scss'),
		}
	];

	const loaded = new Set<string>();

	const maybeLoadStyles = async () => {
		for (const { selectors, importFn } of legacyStyles) {
			const key = selectors.join(',');
			if (loaded.has(key)) continue;

			const found = selectors.some((selector) => document.querySelector(selector));
			if (found) {
				await importFn();
				loaded.add(key);
			}
		}
	};

	// MutationObserver throttling
	let scheduled = false;
	const scheduleCheck = () => {
		if (!scheduled) {
			scheduled = true;
			requestAnimationFrame(async () => {
				scheduled = false;
				await maybeLoadStyles();
			});
		}
	};

	const observer = new MutationObserver(scheduleCheck);

	// Initial check after DOM ready
	const init = () => {
		void maybeLoadStyles();
		observer.observe(document.body, { childList: true, subtree: true });
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
};
